import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/PersonPhones.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonPhones.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { PhoneViewModel, CreatePersonPhoneCommand, UpdatePersonPhoneCommand } from '../../models/Entities/Person/Phone/PhoneModels';

export type KnownAction =
    | Actions.PersonPhones.PersonPhoneGetAllRequestAction
    | Actions.PersonPhones.PersonPhoneGetAllSuccessAction
    | Actions.PersonPhones.PersonPhoneGetAllFailureAction
    | Actions.PersonPhones.PersonPhoneGetByIdRequestAction
    | Actions.PersonPhones.PersonPhoneGetByIdSuccessAction
    | Actions.PersonPhones.PersonPhoneGetByIdFailureAction
    | Actions.PersonPhones.PersonPhoneCreateRequestAction
    | Actions.PersonPhones.PersonPhoneCreateSuccessAction
    | Actions.PersonPhones.PersonPhoneCreateFailureAction
    | Actions.PersonPhones.PersonPhoneUpdateRequestAction
    | Actions.PersonPhones.PersonPhoneUpdateSuccessAction
    | Actions.PersonPhones.PersonPhoneUpdateFailureAction
    | Actions.PersonPhones.PersonPhoneDeleteRequestAction
    | Actions.PersonPhones.PersonPhoneDeleteSuccessAction
    | Actions.PersonPhones.PersonPhoneDeleteFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const actionCreators = {
    GetPhonesByPersonId: (personId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_PHONE_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_PHONE_GETALL_REQUEST, personId });

        Services.GetPhonesByPersonId(token, personId)
            .then((response: PhoneViewModel[]) => {
                dispatch({
                    type: ActionEvents.PERSON_PHONE_GETALL_SUCCESS,
                    personId,
                    payload: response.map((item: PhoneViewModel) => ({ ...item, personId }))
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_GETALL_FAILURE, error });
            });
    },

    GetPhoneById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_PHONE_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_PHONE_GETBYID_REQUEST, id });

        Services.GetPhoneById(token, id)
            .then((response: PhoneViewModel) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_GETBYID_FAILURE, error });
            });
    },

    CreatePhone: (payload: CreatePersonPhoneCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_PHONE_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_PHONE_CREATE_REQUEST, payload });

        Services.CreatePhone(token, payload)
            .then((created: PhoneViewModel) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_CREATE_SUCCESS, payload: { ...created, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_CREATE_FAILURE, error });
            });
    },

    UpdatePhone: (payload: UpdatePersonPhoneCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_PHONE_UPDATE_FAILURE, id: payload.id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_PHONE_UPDATE_REQUEST, payload });

        Services.UpdatePhone(token, payload)
            .then((updated: PhoneViewModel) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_UPDATE_SUCCESS, payload: { ...updated, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_UPDATE_FAILURE, id: payload.id, error });
            });
    },

    DeletePhone: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_PHONE_DELETE_FAILURE, id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_PHONE_DELETE_REQUEST, id });

        Services.DeletePhone(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_PHONE_DELETE_SUCCESS, id });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_PHONE_DELETE_FAILURE, id, error });
            });
    }
};
